import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../lib/AuthContext';

/**
 * Wraps authenticated routes. Kicks off Cognito login if the user has no valid
 * tokens; renders children otherwise. Shows a branded splash while the redirect
 * takes over, and a retry path if login() rejects or the redirect never
 * happens — so a Cognito hiccup can't strand a first-time user on a bare line.
 *
 * The recovery action is a full page reload, never a second login(): a second
 * login() would mint a fresh PKCE verifier and overwrite the one tied to the
 * in-flight authorize request, failing the code exchange. A reload restarts the
 * whole flow cleanly.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const redirected = useRef(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);

  // Redirect to Cognito at most once.
  useEffect(() => {
    if (auth.isAuthenticated || redirected.current) return;
    redirected.current = true;
    auth.login().catch((e) => setFailed(e instanceof Error ? e.message : 'Sign-in could not start.'));
  }, [auth]);

  // If the redirect hasn't taken over the page after a few seconds, surface a
  // manual retry. Kept in its own effect (not gated by `redirected`) so it
  // still arms under React StrictMode's double-invoke in dev.
  useEffect(() => {
    if (auth.isAuthenticated) return;
    const t = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(t);
  }, [auth.isAuthenticated]);

  if (auth.isAuthenticated) return <>{children}</>;

  const reload = () => window.location.reload();

  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <svg width="40" height="40" viewBox="0 0 32 32" fill="none" aria-hidden>
          <rect width="32" height="32" rx="8" fill="#0F172A" />
          <path
            d="M16 6l2.5 5.5L24 13l-4.5 3.5L21 22l-5-3.2L11 22l1.5-5.5L8 13l5.5-1.5L16 6z"
            fill="white"
            opacity=".95"
          />
        </svg>
        <div className="text-sm font-medium tracking-[0.08em] text-white">STARMAP</div>
        {failed ? (
          <>
            <p role="alert" className="max-w-sm text-sm text-[var(--color-heat-red-ink)]">
              Couldn&rsquo;t start sign-in — {failed}
            </p>
            <button
              type="button"
              onClick={reload}
              className="rounded-md border border-white/20 px-4 py-1.5 text-sm text-white/80 hover:text-white"
            >
              Try again
            </button>
          </>
        ) : (
          <>
            <div className="relative h-8 w-8" role="status" aria-label="Redirecting to sign-in">
              <div className="absolute inset-0 rounded-full border-2 border-white/[0.08]" />
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-[var(--color-brand-green)]" />
            </div>
            <p className="text-[length:var(--type-data)] text-[var(--text-secondary)]">Redirecting to sign‑in…</p>
            {slow && (
              <button
                type="button"
                onClick={reload}
                className="rounded-md border border-white/20 px-4 py-1.5 text-sm text-white/80 hover:text-white"
              >
                Taking a while — reload
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
